import { useState } from "react";
import type { Question } from "../types";
import { useAnswers } from "../state/AnswersContext";
import { useRequiredFields } from "../state/RequiredFields";
import { stripHtml } from "../util/text";
import { noteEvent } from "../util/sessionTrack";

type Props = { question: Question };

type LastRun = {
  okCount: number;
  failed: Array<{ name: string; error?: string }>;
};

export default function CalculateButton({ question }: Props) {
  const qCode = question.custrecord_cpqc_question_code;
  const aCode = question._answers[0]?.custrecord_cpqc_answer_code ?? qCode;
  const label =
    stripHtml(
      question._answers[0]?.custrecord_cpqc_answer_label_html as string
    ) ||
    stripHtml(question.custrecord_cpqc_question_label_html) ||
    "Calculate";

  const { runOps } = useAnswers();
  const [last, setLast] = useState<LastRun | null>(null);
  // The next step once its own section is filled in (Generate Housing Options after the
  // sizing fields), so it is the solid button only then; before that it is outlined, and
  // still clickable -- running the ops early is allowed, exactly as in NetSuite.
  const ready = !useRequiredFields().pendingGroups.has(question.custrecord_cpqc_question_group);

  const onClick = () => {
    noteEvent(`calculate:${qCode}`);
    const result = runOps({ [`${qCode}/${aCode}`]: true });
    setLast({
      okCount: result.ran.filter((r) => r.ok).length,
      failed: result.ran
        .filter((r) => !r.ok)
        .map((r) => ({ name: r.name, error: r.error })),
    });
  };

  return (
    <div className="cpq-calculate">
      <button
        type="button"
        className={`cpq-btn ${ready ? "cpq-btn-primary" : "cpq-btn-waiting"}`}
        onClick={onClick}
      >
        {label}
      </button>
      {last && (
        <div className="cpq-calculate-status">
          Ran {last.okCount} operation{last.okCount === 1 ? "" : "s"}.
          {last.failed.length > 0 && (
            <details>
              <summary>{last.failed.length} failed (see DEBUG tab)</summary>
              <ul>
                {last.failed.map((f, i) => (
                  <li key={i}>
                    <code>{f.name}</code>: {f.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
